import { ExchangeRate } from '../../types/currency';
import { MAIN_CURRENCIES } from '../../constants/currencies';

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface RateSource {
  name: string;
  rates: ExchangeRate[];
  timestamp: Date;
  reliability: number; // 0-1 score
}

class RateValidator {
  private readonly maxDeviationPercent = 10; // 10% max deviation between sources
  private readonly minReliabilityScore = 0.7;
  private readonly maxRateAge = 300000; // 5 minutes

  validateSingleRate(rate: ExchangeRate): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic validation
    if (!rate.fromCurrency || !rate.toCurrency) {
      errors.push('Missing currency codes');
    }

    if (!MAIN_CURRENCIES.includes(rate.fromCurrency)) {
      errors.push(`Unsupported currency: ${rate.fromCurrency}`);
    }

    if (rate.rate <= 0 || !isFinite(rate.rate)) {
      errors.push('Invalid rate value');
    }

    if (isNaN(rate.change) || !isFinite(rate.change)) {
      errors.push('Invalid change value');
    }

    // Range validation for NGN rates
    if (rate.toCurrency === 'NGN') {
      const expectedRanges = {
        USD: { min: 1000, max: 3000 },
        EUR: { min: 1200, max: 3500 },
        GBP: { min: 1500, max: 4000 },
        CAD: { min: 800, max: 2500 },
        AUD: { min: 700, max: 2200 },
        XOF: { min: 1, max: 10 }
      };

      const range = expectedRanges[rate.fromCurrency as keyof typeof expectedRanges];
      if (range && (rate.rate < range.min || rate.rate > range.max)) {
        warnings.push(`Rate ${rate.rate} outside expected range ${range.min}-${range.max}`);
      }
    }

    // Timestamp validation
    const rateAge = Date.now() - rate.timestamp.getTime();
    if (rateAge > this.maxRateAge) {
      warnings.push('Rate data is stale');
    }

    // Change validation
    if (Math.abs(rate.change) > 20) {
      warnings.push('Unusually large rate change detected');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  validateMultipleSources(sources: RateSource[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (sources.length < 2) {
      warnings.push('Insufficient sources for cross-validation');
      return { isValid: true, errors, warnings };
    }

    // Group rates by currency
    const ratesByCurrency = new Map<string, number[]>();
    
    sources.forEach(source => {
      if (source.reliability < this.minReliabilityScore) {
        warnings.push(`Low reliability source: ${source.name}`);
      }

      source.rates.forEach(rate => {
        const key = `${rate.fromCurrency}/${rate.toCurrency}`;
        if (!ratesByCurrency.has(key)) {
          ratesByCurrency.set(key, []);
        }
        ratesByCurrency.get(key)!.push(rate.rate);
      });
    });

    // Check for significant deviations
    ratesByCurrency.forEach((rates, pair) => {
      if (rates.length > 1) {
        const avg = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
        const maxDeviation = Math.max(...rates.map(rate => Math.abs((rate - avg) / avg * 100)));

        if (maxDeviation > this.maxDeviationPercent) {
          warnings.push(`High deviation in ${pair}: ${maxDeviation.toFixed(2)}%`);
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  selectBestRates(sources: RateSource[]): ExchangeRate[] {
    const ratesByCurrency = new Map<string, { rate: ExchangeRate; score: number }[]>();

    // Score each rate based on source reliability and freshness
    sources.forEach(source => {
      source.rates.forEach(rate => {
        const key = `${rate.fromCurrency}/${rate.toCurrency}`;
        const ageScore = Math.max(0, 1 - (Date.now() - rate.timestamp.getTime()) / this.maxRateAge);
        const score = source.reliability * 0.7 + ageScore * 0.3;

        if (!ratesByCurrency.has(key)) {
          ratesByCurrency.set(key, []);
        }
        ratesByCurrency.get(key)!.push({ rate, score });
      });
    });

    // Select best rate for each currency
    const bestRates: ExchangeRate[] = [];
    ratesByCurrency.forEach((rates, pair) => {
      const sorted = rates.sort((a, b) => b.score - a.score);
      bestRates.push(sorted[0].rate);
    });

    return bestRates;
  }

  sanitizeRateData(rate: ExchangeRate): ExchangeRate {
    return {
      fromCurrency: rate.fromCurrency.toUpperCase().trim(),
      toCurrency: rate.toCurrency.toUpperCase().trim(),
      rate: Number(Number(rate.rate).toFixed(4)),
      change: Number(Number(rate.change).toFixed(3)),
      timestamp: new Date(rate.timestamp),
      symbol: rate.symbol.trim()
    };
  }
}

export const rateValidator = new RateValidator();